<template>
  <div class="gallery-grid-container">
    <!-- Responsive Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
      <div
        v-for="(image, index) in images"
        :key="index"
        class="grid-item group cursor-pointer relative overflow-hidden rounded-lg bg-gray-100 transition-all duration-300 hover:shadow-xl hover:scale-105"
        @click="openLightbox(index)"
      >
        <!-- Image Container -->
        <div class="aspect-square relative overflow-hidden">
          <img
            :src="image.thumbnailURL"
            :alt="image.caption || `Gallery image ${index + 1}`"
            class="grid-image w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            loading="lazy"
          />
          <!-- Overlay on Hover -->
          <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
            <div class="transform scale-0 group-hover:scale-100 transition-transform duration-300">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
        
        <!-- Caption -->
        <div v-if="image.caption" class="p-3 text-center">
          <p class="text-sm text-gray-700 line-clamp-2">{{ image.caption }}</p>
        </div>
      </div>
    </div>

    <!-- Custom Lightbox Modal -->
    <div 
      v-if="lightboxOpen" 
      class="lightbox-overlay fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
      @click="closeLightbox"
      @keydown.esc="closeLightbox"
    >
      <div class="lightbox-container relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
        <!-- Close Button -->
        <button 
          @click="closeLightbox"
          class="absolute top-4 right-4 z-10 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-3 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          aria-label="Close lightbox"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Navigation Arrows -->
        <button 
          v-if="images.length > 1"
          @click.stop="prevImageLightbox" 
          class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-3 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          aria-label="Previous image"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <button 
          v-if="images.length > 1"
          @click.stop="nextImageLightbox" 
          class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-3 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          aria-label="Next image"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <!-- Main Lightbox Image -->
        <div class="lightbox-image-container flex flex-col items-center justify-center max-w-full max-h-full" @click.stop>
          <img 
            :src="images[lightboxIndex].largeURL" 
            :alt="images[lightboxIndex].caption || `Image ${lightboxIndex + 1}`"
            class="lightbox-image max-w-full max-h-[80vh] w-auto h-auto object-contain"
            @load="onLightboxImageLoad"
          />
          
          <!-- Image Info -->
          <div class="mt-4 text-center text-white">
            <div class="text-lg font-semibold mb-2">{{ lightboxIndex + 1 }} / {{ images.length }}</div>
            <div v-if="images[lightboxIndex].caption" class="text-sm md:text-base max-w-2xl">
              {{ images[lightboxIndex].caption }}
            </div>
          </div>

          <!-- Thumbnail Strip in Lightbox -->
          <div v-if="images.length > 1" class="mt-6 max-w-full overflow-x-auto">
            <div class="flex space-x-2 pb-2">
              <button
                v-for="(image, index) in images"
                :key="index"
                @click="setLightboxImage(index)"
                class="flex-shrink-0 relative rounded overflow-hidden transition-all duration-300"
                :class="{ 'ring-2 ring-white ring-opacity-75': index === lightboxIndex }"
              >
                <img
                  :src="image.thumbnailURL"
                  :alt="`Thumbnail ${index + 1}`"
                  class="w-12 h-12 md:w-16 md:h-16 object-cover transition-opacity duration-300"
                  :class="{ 'opacity-50': index !== lightboxIndex, 'opacity-100': index === lightboxIndex }"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GalleryGridApp',
  props: {
    images: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      lightboxOpen: false,
      lightboxIndex: 0,
    };
  },
  mounted() {
    // Add keyboard event listeners
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    // Lightbox methods
    openLightbox(index) {
      this.lightboxIndex = index;
      this.lightboxOpen = true;
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    },
    closeLightbox() {
      this.lightboxOpen = false;
      document.body.style.overflow = ''; // Restore scroll
    },
    setLightboxImage(index) {
      this.lightboxIndex = index;
    },
    nextImageLightbox() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.images.length;
    },
    prevImageLightbox() {
      this.lightboxIndex = this.lightboxIndex > 0 ? this.lightboxIndex - 1 : this.images.length - 1;
    },
    onLightboxImageLoad() {
      // Image loaded successfully
    },

    // Keyboard navigation
    handleKeydown(event) {
      if (!this.lightboxOpen) return;
      
      switch (event.key) {
        case 'Escape':
          this.closeLightbox();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.prevImageLightbox();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.nextImageLightbox();
          break;
      }
    },
  },
};
</script>

<style scoped>
.gallery-grid-container {
  width: 100%;
  max-width: 80rem;
  margin: 0 auto;
}

.grid-item {
  transition: all 0.3s ease;
}

.grid-item:hover {
  transform: scale(1.05);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.grid-image {
  transition: transform 0.3s ease;
}

.lightbox-overlay {
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease-out;
}

.lightbox-image {
  animation: zoomIn 0.3s ease-out;
}

/* Text truncation */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Mobile optimizations */
@media (max-width: 640px) {
  .gallery-grid-container {
    padding: 0 1rem;
  }
  
  .grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  .lightbox-image {
    max-height: 70vh;
  }
}

@media (max-width: 480px) {
  .grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

/* Ensure proper aspect ratio on mobile */
.aspect-square {
  aspect-ratio: 1 / 1;
}

/* Smooth scrolling for thumbnail strip */
.overflow-x-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

.overflow-x-auto::-webkit-scrollbar {
  height: 4px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}
</style>
